import React, { useState, useMemo } from 'react';
import './TradeSimulator.css';

export default function TradeSimulator({ scores, analysisResult }) {
  const [action, setAction] = useState('buy');
  const [symbol, setSymbol] = useState('');
  const [shares, setShares] = useState('');
  const [price, setPrice] = useState('');
  const [impact, setImpact] = useState(null);

  const currentTotalValue = useMemo(() => {
    return (scores || []).reduce((sum, s) => sum + (s.position_value || 0), 0);
  }, [scores]);

  const currentWeightedConviction = useMemo(() => {
    if (!scores?.length || currentTotalValue <= 0) return 0;
    return scores.reduce((sum, s) => {
      const w = (s.position_value || 0) / currentTotalValue;
      return sum + (s.combined_conviction || 0) * w;
    }, 0);
  }, [scores, currentTotalValue]);

  const simulate = () => {
    const numShares = parseFloat(shares);
    const numPrice = parseFloat(price);
    if (!symbol || isNaN(numShares) || isNaN(numPrice) || numShares <= 0 || numPrice <= 0) return;

    const tradeValue = numShares * numPrice;
    const sign = action === 'buy' ? 1 : -1;
    const newTotalValue = currentTotalValue + sign * tradeValue;

    // Check if symbol exists in portfolio
    const existingScore = (scores || []).find(s => s.symbol.toUpperCase() === symbol.toUpperCase());
    const existingConviction = existingScore?.combined_conviction || 50; // Default 50 for new stocks

    // Recalculate weighted conviction
    let newWeightedConviction;
    if (newTotalValue > 0) {
      // Adjust each position's weight
      newWeightedConviction = (scores || []).reduce((sum, s) => {
        let posValue = s.position_value || 0;
        if (s.symbol.toUpperCase() === symbol.toUpperCase()) {
          posValue += sign * tradeValue;
        }
        if (posValue <= 0) return sum;
        const w = posValue / newTotalValue;
        return sum + (s.combined_conviction || 0) * w;
      }, 0);

      // If buying a new stock not in portfolio
      if (!existingScore && action === 'buy') {
        const newW = tradeValue / newTotalValue;
        newWeightedConviction = currentWeightedConviction * (1 - newW) + existingConviction * newW;
      }
    } else {
      newWeightedConviction = 0;
    }

    // Concentration impact
    const targetValue = action === 'buy'
      ? (existingScore ? (existingScore.position_value || 0) + tradeValue : tradeValue)
      : Math.max(0, (existingScore?.position_value || 0) - tradeValue);
    const newConcentration = newTotalValue > 0 ? (targetValue / newTotalValue) * 100 : 0;

    setImpact({
      newTotalValue,
      valueDelta: sign * tradeValue,
      newWeightedConviction,
      convictionDelta: newWeightedConviction - currentWeightedConviction,
      newConcentration,
      existingConviction: existingScore?.combined_conviction,
      isNewPosition: !existingScore && action === 'buy',
    });
  };

  return (
    <div className="trade-simulator">
      <h3 className="ts-title">Trade Impact Simulator</h3>
      <div className="ts-form">
        <div className="ts-action-toggle">
          <button className={`ts-action-btn ${action === 'buy' ? 'active buy' : ''}`} onClick={() => setAction('buy')}>Buy</button>
          <button className={`ts-action-btn ${action === 'sell' ? 'active sell' : ''}`} onClick={() => setAction('sell')}>Sell</button>
        </div>
        <input className="ts-input" placeholder="Symbol (e.g. AAPL)" value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} />
        <input className="ts-input" type="number" placeholder="Shares" value={shares} onChange={e => setShares(e.target.value)} />
        <input className="ts-input" type="number" placeholder="Price ($)" value={price} onChange={e => setPrice(e.target.value)} />
        <button className="ts-simulate-btn" onClick={simulate}>Simulate</button>
      </div>
      {impact && (
        <div className="ts-results">
          <div className="ts-result-item">
            <span className="ts-label">Portfolio Value</span>
            <span className="ts-value">${impact.newTotalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            <span className={`ts-delta ${impact.valueDelta >= 0 ? 'positive' : 'negative'}`}>
              {impact.valueDelta >= 0 ? '+' : ''}${impact.valueDelta.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="ts-result-item">
            <span className="ts-label">Weighted Conviction</span>
            <span className="ts-value">{impact.newWeightedConviction.toFixed(1)}</span>
            <span className={`ts-delta ${impact.convictionDelta >= 0 ? 'positive' : 'negative'}`}>
              {impact.convictionDelta >= 0 ? '+' : ''}{impact.convictionDelta.toFixed(1)}
            </span>
          </div>
          <div className="ts-result-item">
            <span className="ts-label">{symbol} Concentration</span>
            <span className="ts-value">{impact.newConcentration.toFixed(1)}%</span>
            {impact.newConcentration > 25 && <span className="ts-warning">High concentration</span>}
          </div>
          {impact.isNewPosition && (
            <div className="ts-note">New position (using default conviction of 50)</div>
          )}
        </div>
      )}
    </div>
  );
}
